import React, { useMemo } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { useTheme } from '../../styles/ThemeProvider';
import type { SiblingVariant } from '../../lib/variantOptions';

/**
 * Maps the catalog's *unnormalized* `product_variants.attributes` keys onto a
 * small set of display axes, and records whether each axis is price-relevant
 * (Q1 in the plan). The catalog has real key drift — `color` vs `colorway`,
 * `size` vs `shoe_size`/`case_size`, `storage` vs `capacity`, `memory` vs
 * `ram`, `chip` vs `processor`, plus spaced variants like `case color`. We
 * fold synonyms so two siblings keyed `color` and `colorway` group under one
 * "Colour" axis rather than reading as two unrelated facets.
 *
 * Price-relevance follows Q1: storage / memory / cpu are price axes;
 * `colorway` (footwear) is price-relevant but plain `color` is record-only;
 * size and material are record-only. Unknown keys render raw and record-only.
 */
const ATTRIBUTE_ALIASES: Record<string, { axis: string; priceRelevant: boolean }> = {
  storage: { axis: 'Storage', priceRelevant: true },
  capacity: { axis: 'Storage', priceRelevant: true },
  memory: { axis: 'Memory', priceRelevant: true },
  ram: { axis: 'Memory', priceRelevant: true },
  chip: { axis: 'Chip', priceRelevant: true },
  processor: { axis: 'Chip', priceRelevant: true },
  colorway: { axis: 'Colour', priceRelevant: true },
  color: { axis: 'Colour', priceRelevant: false },
  exterior_color: { axis: 'Colour', priceRelevant: false },
  case_color: { axis: 'Colour', priceRelevant: false },
  'case color': { axis: 'Colour', priceRelevant: false },
  band_color: { axis: 'Colour', priceRelevant: false },
  frame_color: { axis: 'Colour', priceRelevant: false },
  size: { axis: 'Size', priceRelevant: false },
  shoe_size: { axis: 'Size', priceRelevant: false },
  case_size: { axis: 'Size', priceRelevant: false },
  screen_size: { axis: 'Size', priceRelevant: false },
  material: { axis: 'Material', priceRelevant: false },
  case_material: { axis: 'Material', priceRelevant: false },
  'case material': { axis: 'Material', priceRelevant: false },
};

function titleCase(raw: string): string {
  return raw
    .replace(/[_\s]+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Currency-aware headline price. Each sibling carries an explicit ISO
 * currency code (`price.currency`), so the previous `$<avg> <code>` form
 * mislabelled anything non-USD (e.g. "$165 GBP"). `Intl.NumberFormat` —
 * the same formatting API used for portfolio values elsewhere — renders
 * the symbol that matches the code, with cents dropped to match the
 * compact headline style. The locale is pinned to `en-US` to match the
 * portfolio-value formatting elsewhere and keep the symbol stable
 * regardless of device locale. An unrecognised code throws inside the
 * constructor, so we fall back to a plain amount + code suffix.
 */
function formatVariantPrice(avg: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(avg);
  } catch {
    return `${Math.round(avg)} ${currency}`;
  }
}

/** Resolve a raw JSONB attribute key to its display axis + price-relevance. */
function normalizeAttributeKey(rawKey: string): { axis: string; priceRelevant: boolean } {
  return ATTRIBUTE_ALIASES[rawKey.toLowerCase()] ?? { axis: titleCase(rawKey), priceRelevant: false };
}

interface DisplayAttribute {
  axis: string;
  value: string;
  priceRelevant: boolean;
}

interface VariantPickerProps {
  showPrices?: boolean;
  variants: SiblingVariant[];
  onSelect: (variant: SiblingVariant) => void;
}

/**
 * Catalog-bound variant picker. Renders each sibling variant as a selectable
 * row showing its *distinguishing* attributes (the axes whose value differs
 * across the set), its headline price (or "Price unavailable" when the
 * sibling is catalogued but unpriced — Q3), and a marker on the current one.
 *
 * Selection is structured-only: tapping a row fires `onSelect` with the
 * chosen `SiblingVariant`; the caller owns the write. With fewer than two
 * variants there is nothing to choose between, so the picker renders an empty
 * state rather than a dead single-option list (Q2 — callers also gate on ≥2).
 */
export function VariantPicker({ variants, onSelect, showPrices = true }: VariantPickerProps) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          gap: theme.spacing.sm,
        },
        empty: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
        },
        option: {
          borderWidth: 1,
          borderColor: theme.colors.border,
          borderRadius: theme.radius.radiusMd,
          padding: theme.spacing.md,
          gap: theme.spacing.xs,
          backgroundColor: theme.colors.backgroundSurface,
        },
        optionCurrent: {
          borderColor: theme.colors.accent,
        },
        headerRow: {
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        },
        currentBadge: {
          ...theme.typography.xs,
          color: theme.colors.accent,
          textTransform: 'uppercase',
        },
        price: {
          ...theme.typography.sm,
          color: theme.colors.textPrimary,
        },
        priceUnavailable: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          fontStyle: 'italic',
        },
        attrRow: {
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing.xs,
          flexWrap: 'wrap',
        },
        attrText: {
          ...theme.typography.sm,
          color: theme.colors.textPrimary,
        },
        relevantTag: {
          ...theme.typography.xs,
          color: theme.colors.accent,
        },
      }),
    [theme],
  );

  // Determine which axes are "distinguishing": those whose value differs
  // across the set, or that are present on some variants but not all. Only
  // these are worth showing — shared attributes are noise.
  const distinguishingAxes = useMemo(() => {
    const valuesByAxis = new Map<string, Set<string>>();
    const countByAxis = new Map<string, number>();
    for (const v of variants) {
      const seenAxes = new Set<string>();
      for (const [rawKey, rawVal] of Object.entries(v.attributes)) {
        const { axis } = normalizeAttributeKey(rawKey);
        if (!valuesByAxis.has(axis)) valuesByAxis.set(axis, new Set());
        valuesByAxis.get(axis)!.add(String(rawVal));
        seenAxes.add(axis);
      }
      for (const axis of seenAxes) {
        countByAxis.set(axis, (countByAxis.get(axis) ?? 0) + 1);
      }
    }
    const distinguishing = new Set<string>();
    for (const [axis, values] of valuesByAxis) {
      if (values.size > 1 || (countByAxis.get(axis) ?? 0) < variants.length) {
        distinguishing.add(axis);
      }
    }
    return distinguishing;
  }, [variants]);

  if (variants.length < 2) {
    return (
      <View style={styles.container}>
        <Text style={styles.empty} testID="variant-picker-empty">
          No other variants to choose from.
        </Text>
      </View>
    );
  }

  const displayAttributes = (variant: SiblingVariant): DisplayAttribute[] => {
    const attrs: DisplayAttribute[] = [];
    for (const [rawKey, rawVal] of Object.entries(variant.attributes)) {
      const { axis, priceRelevant } = normalizeAttributeKey(rawKey);
      if (distinguishingAxes.has(axis)) {
        attrs.push({ axis, value: String(rawVal), priceRelevant });
      }
    }
    return attrs;
  };

  return (
    <View style={styles.container}>
      {variants.map((variant) => {
        const attrs = displayAttributes(variant);
        return (
          <TouchableOpacity
            key={variant.variantId}
            testID={`variant-option-${variant.variantId}`}
            accessibilityRole="button"
            style={[styles.option, variant.isCurrent && styles.optionCurrent]}
            onPress={() => onSelect(variant)}
          >
            <View style={styles.headerRow}>
              {showPrices && (variant.price ? (
                <Text style={styles.price}>
                  {formatVariantPrice(variant.price.avg, variant.price.currency)}
                </Text>
              ) : (
                <Text style={styles.priceUnavailable}>Price unavailable</Text>
              ))}
              {variant.isCurrent && (
                <Text style={styles.currentBadge} testID="variant-current-badge">
                  Current
                </Text>
              )}
            </View>
            {attrs.map((attr) => (
              <View key={attr.axis} style={styles.attrRow}>
                <Text style={styles.attrText}>
                  {attr.axis}: {attr.value}
                </Text>
                {showPrices && attr.priceRelevant && (
                  <Text style={styles.relevantTag} testID={`relevant-${attr.axis}`}>
                    Affects price
                  </Text>
                )}
              </View>
            ))}
          </TouchableOpacity>
        );
      })}
    </View>
  );
}
